import React, { useEffect, useState } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { PageLayout } from '@src/common/PageLayout';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Card } from '@uhg-abyss/web/ui/Card';
import { CLIA_PRACTICES } from '@src/state/tinRegistration/utils';
import { useTinRegistration } from '@src/state/tinRegistration';
import { uniqueId } from 'lodash';
import { FileUpload as FileUploadComp } from '@src/common/FileUploadV2';
import { PracticeType } from './PracticeType';
import { RestrictionInput } from './RestrictionInput';
import { NpiNumbers } from './NpiNumbers';
import { License } from './License';

const NpiInfo = styled('div', {
  padding: '0 0.5625rem 0.7rem 0.825rem',
  borderRadius: '0.1875rem 0.1875rem 0 0',
  margin: '0 1rem 1rem 1rem',
});

const LicenseInfo = styled('div', {
  padding: '0 0.5625rem 0.7rem 0.825rem',
  borderRadius: '0.1875rem 0.1875rem 0 0',
  margin: '0 1rem 1rem 1rem',
});

const TextStyle = styled('div', {
  margin: '10px 10px 10px 30px',
});

const TextStylePracticeType = styled('div', {
  margin: '10px 10px 10px 30px',
  width: '40%',
});

const RestrictionInfo = styled('div', {
  lineHeight: '150%',
  width: '388px',
  fontSize: '14px',
  fontWeight: 600,
  display: 'inline',
});

const CardInfo = styled('div', {
  marginLeft: '-20px',
});

export const PracticeInfoComponent = () => {
  const form = useForm();
  const restrictionSelection = form.getValues(
    'practiceInformation.restrictionSelection'
  );
  const practiceType = form.watch('practiceInformation.practiceTypeSelection');
  const cliaIdNumber = form.getValues('practiceInformation.cliaIdentification');
  const medicareId = form.getValues('practiceInformation.medicareId');
  const cliaAttestation = form.getValues('practiceInformation.cliaAttestation');
  const medicareAttestation = form.getValues(
    'practiceInformation.medicareAttestation'
  );

  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isCmsCheckboxDisabled, setCmsCheckboxDisabled] = useState(false);
  const [isMedicareCheckboxDisabled, setMedicareCheckboxDisabled] =
    useState(false);
  const { tinDraft } = useTinRegistration.state();
  const onUpload = (e) => {
    if (e === false) {
      setCmsCheckboxDisabled(false);
    }
    setUploadSuccess(e);
  };

  useEffect(() => {
    if (tinDraft && tinDraft.returnToRegistration) {
      if (cliaIdNumber != null && cliaIdNumber !== '' && uploadSuccess) {
        setCmsCheckboxDisabled(true);
      } else {
        setCmsCheckboxDisabled(false);
      }

      if (medicareId === null) {
        setMedicareCheckboxDisabled(false);
      } else if (medicareId !== '' && !medicareAttestation) {
        setMedicareCheckboxDisabled(true);
      } else if (medicareId !== '' && medicareAttestation) {
        setMedicareCheckboxDisabled(true);
      } else {
        setMedicareCheckboxDisabled(false);
      }
    }
  });

  useEffect(() => {
    setCmsCheckboxDisabled(false);

    if (cliaIdNumber != null && cliaIdNumber !== '' && uploadSuccess) {
      setCmsCheckboxDisabled(true);
      form.setValue('practiceInformation.cliaAttestation', false, {
        errors: { required: false },
        touched: false,
      });
    } else if (cliaIdNumber === '' && uploadSuccess && cliaAttestation) {
      form.setValue('practiceInformation.cliaAttestation', cliaAttestation, {
        errors: { required: false },
        touched: false,
      });
    } else if (cliaIdNumber !== '' && !uploadSuccess && cliaAttestation) {
      form.setValue('practiceInformation.cliaAttestation', cliaAttestation, {
        errors: { required: false },
        touched: false,
      });
    } else if (cliaIdNumber === '' && !uploadSuccess && cliaAttestation) {
      form.setValue('practiceInformation.cliaAttestation', cliaAttestation, {
        errors: { required: false },
        touched: false,
      });
    } else if (cliaIdNumber === '' && !uploadSuccess) {
      form.setValue('practiceInformation.cliaAttestation', cliaAttestation, {
        errors: { required: true },
        touched: false,
      });
    } else {
      form.setValue('practiceInformation.cliaAttestation', cliaAttestation, {
        errors: { required: true },
        touched: false,
      });
    }
  }, [cliaIdNumber, uploadSuccess, cliaAttestation, practiceType]);

  useEffect(() => {
    setMedicareCheckboxDisabled(false);
    if (medicareId && !medicareAttestation) {
      setMedicareCheckboxDisabled(true);
      form.setValue('practiceInformation.medicareAttestation', false, {
        errors: { required: false },
        touched: false,
      });
    } else if (medicareId !== '' && medicareAttestation) {
      setMedicareCheckboxDisabled(true);
      form.setValue('practiceInformation.medicareAttestation', false, {
        errors: { required: false },
        touched: false,
      });
    } else if (medicareId === '' && medicareAttestation) {
      form.setValue(
        'practiceInformation.medicareAttestation',
        medicareAttestation,
        {
          errors: { required: false },
          touched: false,
        }
      );
    } else if (medicareId === '' && !medicareAttestation) {
      form.setValue(
        'practiceInformation.medicareAttestation',
        medicareAttestation,
        {
          errors: { required: true },
          touched: false,
        }
      );
    }
  }, [medicareId]);

  useEffect(() => {
    if (medicareId === '' && !medicareAttestation) {
      form.setValue(
        'practiceInformation.medicareAttestation',
        medicareAttestation,
        {
          errors: { required: true },
          touched: false,
        }
      );
    } else if (medicareId !== '' && !medicareAttestation) {
      form.setValue(
        'practiceInformation.medicareAttestation',
        medicareAttestation,
        {
          errors: { required: false },
          touched: false,
        }
      );
    }
  }, [medicareAttestation]);

  return (
    <PageLayout>
      <Card id="practice-data-label" header="Practice data">
        <Card.Section>
          <CardInfo>
            <TextStyle id="regtype">
              <RadioGroup
                label="Registration type"
                helper="An individual practice involves one physician who provides
              medical care. A group/facility practice involves two or
              more physicians who provide medical care/supply/service."
                model="practiceInformation.registrationTypeSelection"
                aria-labelledby="radio-provider"
                display="row"
                validators={{
                  required: (value) => {
                    return !value;
                  },
                }}
                required
                errorMessage="Please select one."
              >
                <RadioGroup.Radio
                  id="radio-provider-individual"
                  value="Individual"
                  label="Individual"
                />
                <RadioGroup.Radio
                  id="radio-provider-group"
                  value="Group"
                  label=" Group/Facility"
                />
              </RadioGroup>
            </TextStyle>
            <div style={{ width: '83.45%' }}>
              <PracticeType />
            </div>
            {practiceType && CLIA_PRACTICES.includes(practiceType) && (
              <React.Fragment>
                <TextStyle>
                  <div style={{ display: 'flex' }}>
                    <div style={{ width: '264px' }}>
                      <React.Fragment>
                        <TextInput
                          label="CLIA Identification Number (optional)"
                          model="practiceInformation.cliaIdentification"
                          formatter={(value) => {
                            return value.replace(
                              /[!@#$%^&*)({}<>,.[\]+_`~=\\|';":?]/g,
                              ''
                            );
                          }}
                          validators={{
                            invalid: (value) => {
                              return value !== '' &&
                                value &&
                                (value.length < 3 ||
                                  value.includes(' ') ||
                                  !value.match(/[[a-zA-Z0-9\]]/g))
                                ? 'Please enter a valid CLIA ID number. Enter N/A if not applicable.'
                                : '';
                            },
                          }}
                          data-testid="practice-info-clia-id"
                          maxLength={10}
                          isClearable
                          name={`clia-number-input-${uniqueId()}`}
                          autoComplete="none"
                        />
                      </React.Fragment>
                    </div>

                    <div
                      style={{ display: 'inline-block', marginLeft: '1rem' }}
                    >
                      <FileUploadComp
                        id="upload-clia-input"
                        model="practiceInformation.cliaFile"
                        label="CMS 116"
                        onUpload={onUpload}
                        attest={cliaAttestation}
                        required={!cliaAttestation}
                      />
                    </div>
                  </div>
                </TextStyle>
                <TextStyle>
                  <Checkbox
                    id="practice-type-clia-attestation-checkbox"
                    model="practiceInformation.cliaAttestation"
                    label="By not attaching CMS 116 and/or inputting CLIA ID above, I
                    attest that it is not applicable for my practice or is
                    pending issuance from CMS. I understand that my TIN
                    registration may take longer to verify or may be rejected."
                    isDisabled={isCmsCheckboxDisabled}
                    validators={{
                      required: cliaIdNumber
                        ? ''
                        : 'Must check box before proceeding.',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 'normal',
                      }}
                    >
                      By not attaching CMS 116 and/or inputting CLIA ID above, I
                      attest that it is not applicable for my practice or is
                      pending issuance from CMS. I understand that my TIN
                      registration may take longer to verify or may be rejected.
                    </div>
                  </Checkbox>
                </TextStyle>
              </React.Fragment>
            )}

            {practiceType === 'Ambulatory Surgery Center' && (
              <React.Fragment>
                <TextStyle>
                  <div style={{ width: '268px' }}>
                    <TextInput
                      id="medicare-id"
                      label="Medicare ID (Optional)"
                      model="practiceInformation.medicareId"
                      formatter={(value) => {
                        return value.replace(
                          /[!@#$%^&*)({}<>,.[\]+_`~=\\|';":?]/g,
                          ''
                        );
                      }}
                      validators={{
                        invalid: (value) => {
                          return value &&
                            value !== '' &&
                            (value.length < 3 ||
                              value.includes(' ') ||
                              !value.match(/[[a-zA-Z0-9\]]/g))
                            ? 'Please enter a valid Medicare ID number. Enter N/A if not applicable.'
                            : '';
                        },
                      }}
                      data-testid="practice-info-medicare-id"
                      maxLength={11}
                      isClearable
                      name={`medicare-id-input-${uniqueId()}`}
                      autoComplete="none"
                    />
                  </div>
                </TextStyle>
                <TextStyle>
                  <React.Fragment>
                    <Checkbox
                      model="practiceInformation.medicareAttestation"
                      id="practice-type-medicare-attestation-checkbox"
                      isDisabled={isMedicareCheckboxDisabled}
                      validators={{
                        required: medicareId
                          ? ''
                          : 'Must check box before proceeding.',
                      }}
                    >
                      By not inputting Medicare ID above, I attest that it is
                      not applicable for my practice or is pending issuance from
                      CMS. I understand that my registration may take longer to
                      verify or may be rejected.
                    </Checkbox>
                  </React.Fragment>
                </TextStyle>
              </React.Fragment>
            )}
            <TextStylePracticeType id="practice-data-restriction-info-label">
              <RadioGroup
                label={
                  <RestrictionInfo>
                    Has any individuals in your practice ever had their
                    professional license relinquished, denied, suspended,
                    revoked, restricted, or have you ever been subject to a
                    fine, reprimand, consent order, probation or any conditions
                    or limitations by any state or professional licensing board?
                  </RestrictionInfo>
                }
                model="practiceInformation.restrictionSelection"
                aria-labelledby="radio-restriction"
                display="row"
                validators={{
                  required: (value) => {
                    return !value;
                  },
                }}
                errorMessage="Please select one."
                required
              >
                <RadioGroup.Radio
                  id="radio-restriction-yes"
                  value="Yes"
                  label="Yes"
                />
                <RadioGroup.Radio
                  id="radio-restriction-no"
                  value="No"
                  label="No"
                />
              </RadioGroup>
            </TextStylePracticeType>
            {restrictionSelection === 'Yes' && <RestrictionInput />}
            <LicenseInfo>
              <License />
            </LicenseInfo>
            <NpiInfo>
              <NpiNumbers />
            </NpiInfo>
          </CardInfo>
        </Card.Section>
      </Card>
    </PageLayout>
  );
};
